import React, { useState, useCallback } from 'react';
import { BarChart2, Save, RefreshCw, Download, Cpu } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import LoanParametersSection from './LoanParametersSection';
import AdditionalParametersSection from './AdditionalParametersSection';
import RiskAssessmentSection from './RiskAssessmentSection';
import TechnologyFocusSection from './TechnologyFocusSection';

/**
 * Компонент формы калькулятора инвестиций в ИИ
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors,
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  investmentSchedule,
  setInvestmentSchedule,
  saveInvestmentPlan,
  investmentStrategies,
  riskLevels,
  aiSectors,
  technologyFocus,
  geographicFocus
}) => {
  // Состояние для сохранения инвестиционного плана
  const [saveInvestmentName, setSaveInvestmentName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  // Функции парсинга числовых значений
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };

  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' 
      ? checked 
      : (type === 'select-multiple' 
        ? Array.from(e.target.selectedOptions).map(option => option.value)
        : value);

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Логика автоматической настройки параметров
      if (name === 'riskLevel') {
        switch (fieldValue) {
          case 'low':
            newState.investmentType = 'etf';
            newState.aiSectors = ['software', 'cloud'];
            newState.technologyFocus = ['machine_learning'];
            break;
          case 'moderate':
            newState.investmentType = 'diversified';
            newState.aiSectors = ['software', 'hardware', 'cloud'];
            newState.technologyFocus = ['machine_learning', 'neural_networks'];
            break;
          case 'high':
            newState.investmentType = 'focused';
            newState.aiSectors = ['robotics'];
            newState.technologyFocus = ['neural_networks', 'computer_vision'];
            break;
        }
      }

      return newState;
    });

    // Очистка ошибок для измененного поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      initialInvestment, 
      investmentTerm, 
      monthlyContribution,
      riskLevel,
      investmentType,
      aiSectors,
      technologyFocus,
      geographicFocus
    } = formData;

    // Валидация начальной суммы инвестиций
    const numInitialInvestment = parseFloatOrDefault(initialInvestment, -1);
    if (numInitialInvestment <= 0) {
      newErrors.initialInvestment = 'Сумма должна быть положительной.';
    }
    if (numInitialInvestment > 50000000) {
      newErrors.initialInvestment = 'Максимальная сумма 50 млн ₽.';
    }

    // Валидация срока инвестирования
    const numInvestmentTerm = parseFloatOrDefault(investmentTerm, -1);
    if (numInvestmentTerm <= 0) {
      newErrors.investmentTerm = 'Срок должен быть положительным.';
    }
    if (numInvestmentTerm > 30) {
      newErrors.investmentTerm = 'Максимальный срок 30 лет.';
    }

    // Валидация ежемесячного взноса
    const numMonthlyContribution = parseFloatOrDefault(monthlyContribution, -1);
    if (numMonthlyContribution < 0) {
      newErrors.monthlyContribution = 'Сумма не может быть отрицательной.';
    }
    if (numMonthlyContribution > numInitialInvestment * 2) {
      newErrors.monthlyContribution = 'Сумма взноса слишком высокая.';
    }

    // Валидация уровня риска
    if (!riskLevel) {
      newErrors.riskLevel = 'Выберите уровень риска.';
    }

    // Валидация типа инвестиционной стратегии
    if (!investmentType) {
      newErrors.investmentType = 'Выберите тип стратегии.';
    }

    // Валидация секторов ИИ
    if (!aiSectors || aiSectors.length === 0) {
      newErrors.aiSectors = 'Выберите хотя бы один сектор ИИ.';
    }

    // Валидация технологического фокуса
    if (!technologyFocus || technologyFocus.length === 0) {
      newErrors.technologyFocus = 'Выберите хотя бы одну технологию.';
    }

    // Валидация географического фокуса
    if (!geographicFocus || geographicFocus.length === 0) {
      newErrors.geographicFocus = 'Выберите хотя бы один регион.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета инвестиционного плана
  const calculateInvestmentPlan = useCallback(() => {
    // Проверка валидности формы
    if (!validateForm()) {
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setInvestmentSchedule([]);

    // Эмуляция расчета с задержкой
    setTimeout(() => {
      try {
        // Подготовка входных данных
        const initialInvestment = parseFloatOrDefault(formData.initialInvestment);
        const investmentTermYears = parseFloatOrDefault(formData.investmentTerm);
        const monthlyContribution = parseFloatOrDefault(formData.monthlyContribution);
        
        // Коэффициенты доходности в зависимости от уровня риска и стратегии
        const riskMultipliers = {
          'low': {
            'etf': 0.05,     // Консервативная ETF-стратегия
            'diversified': 0.07
          },
          'moderate': {
            'diversified': 0.10, // Сбалансированная стратегия
            'focused': 0.12
          },
          'high': {
            'focused': 0.15,     // Агрессивная стратегия
            'etf': 0.13
          }
        };

        // Выбор множителя доходности
        const riskMultiplier = riskMultipliers[formData.riskLevel][formData.investmentType] || 0.10;

        // Расчет графика инвестиций
        const schedule = [];
        let currentBalance = initialInvestment;
        let totalContributions = initialInvestment;
        let totalInterest = 0;

        for (let month = 1; month <= investmentTermYears * 12; month++) {
          // Ежемесячное начисление процентов
          const monthlyRate = riskMultiplier / 12;
          const monthlyInterest = currentBalance * monthlyRate;
          currentBalance += monthlyInterest;
          totalInterest += monthlyInterest;

          // Ежемесячный взнос
          currentBalance += monthlyContribution;
          totalContributions += monthlyContribution;

          // Формирование записи в графике
          schedule.push({
            month,
            contribution: monthlyContribution,
            interest: monthlyInterest,
            balance: currentBalance,
            cumulativeContribution: totalContributions,
            cumulativeInterest: totalInterest
          });
        }

        // Формирование результатов
        const finalResults = {
          initialInvestment,
          investmentTerm: investmentTermYears,
          monthlyContribution,
          riskLevel: formData.riskLevel,
          investmentType: formData.investmentType,
          totalContributions,
          finalBalance: currentBalance,
          totalInterest,
          averageAnnualReturn: riskMultiplier * 100,
          aiSectors: formData.aiSectors,
          technologyFocus: formData.technologyFocus,
          geographicFocus: formData.geographicFocus,
          profitability: {
            totalProfit: currentBalance - totalContributions,
            profitabilityPercent: ((currentBalance - totalContributions) / totalContributions) * 100
          }
        };

        setResults(finalResults);
        setInvestmentSchedule(schedule);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}` });
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, setErrors, setResults, setInvestmentSchedule, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      initialInvestment: '100000',
      investmentTerm: '5',
      riskLevel: 'moderate',
      aiSectors: ['software', 'hardware'],
      reinvestmentStrategy: 'compound',
      monthlyContribution: '10000',
      targetCompanies: [],
      investmentType: 'diversified',
      technologyFocus: ['machine_learning', 'neural_networks'],
      geographicFocus: ['usa', 'global']
    });
    setResults(null);
    setInvestmentSchedule([]);
    setErrors({});
    setSaveInvestmentName('');
    setShowSaveDialog(false);
  };

  // Экспорт графика инвестиций в CSV
  const exportToCSV = () => {
    if (!investmentSchedule.length || !results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Формирование CSV-контента
    const headers = [
      'Месяц', 
      'Взнос', 
      'Проценты', 
      'Баланс', 
      'Совокупный взнос', 
      'Совокупные проценты'
    ];
    const rows = investmentSchedule.map(item => [
      item.month,
      item.contribution.toFixed(2).replace('.', ','),
      item.interest.toFixed(2).replace('.', ','),
      item.balance.toFixed(2).replace('.', ','),
      item.cumulativeContribution.toFixed(2).replace('.', ','),
      item.cumulativeInterest.toFixed(2).replace('.', ',')
    ]);

    // Добавление итоговой информации
    const infoRows = [
      ['Начальная сумма:', results.initialInvestment.toFixed(2).replace('.', ',')],
      ['Срок инвестирования:', `${results.investmentTerm} лет`],
      ['Ежемесячный взнос:', results.monthlyContribution.toFixed(2).replace('.', ',')],
      ['Уровень риска:', results.riskLevel],
      ['Тип стратегии:', results.investmentType],
      ['Общий взнос:', results.totalContributions.toFixed(2).replace('.', ',')],
      ['Итоговый баланс:', results.finalBalance.toFixed(2).replace('.', ',')],
      ['Доходность:', `${results.profitability.profitabilityPercent.toFixed(2).replace('.', ',')}%`],
      ['Сектора ИИ:', results.aiSectors.join(', ')],
      ['Технологический фокус:', results.technologyFocus.join(', ')],
      ['Географический фокус:', results.geographicFocus.join(', ')]
    ];

    // Создание и загрузка CSV
    const csvContent = 
      '\uFEFF' + // BOM для корректной кодировки
      [...infoRows.map(row => row.join(';')), 
       '', 
       headers.join(';'), 
       ...rows.map(row => row.join(';'))
      ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'инвестиции_в_ИИ.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения инвестиционного плана
  const handleSaveInvestmentPlan = () => {
    if (showSaveDialog) {
      const savedResult = saveInvestmentPlan(saveInvestmentName || null);
      if (savedResult) {
        alert(`Инвестиционный план "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveInvestmentName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateInvestmentPlan();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Cpu className="w-6 h-6 text-indigo-500" />
          Параметры инвестиций в ИИ</h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры инвестиций */}
        <LoanParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          investmentStrategies={investmentStrategies}
        />

        {/* Дополнительные параметры */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Оценка рисков */}
        <RiskAssessmentSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          riskLevels={riskLevels}
          aiSectors={aiSectors}
        />

        {/* Технологический фокус */}
        <TechnologyFocusSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          technologyFocus={technologyFocus}
          geographicFocus={geographicFocus}
        />

        {/* Диалог сохранения инвестиционного плана */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveInvestmentName}
                onChange={(e) => setSaveInvestmentName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveInvestmentPlan}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать инвестиции'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {investmentSchedule.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать график (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveInvestmentPlan}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить план
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">
            Пожалуйста, исправьте ошибки в форме.
          </p>
        )}
      </div>
    </form>
  );
}
export default CalculationForm;